import React, { useState, useEffect, useContext, Fragment, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Skeleton from "@mui/material/Skeleton";
import Paper from "@mui/material/Paper";

import Map from "@/components/Map/Map";

// OpenLayers
import proj4 from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { get as OlGetProjection, Projection } from "ol/proj";
import OlMapBrowserEvent from "ol/MapBrowserEvent";
import OlBaseLayer from "ol/layer/Base";
import OlCollection from "ol/Collection";
import OlSourceVector from "ol/source/Vector";
import Geometry from "ol/geom/Geometry";
import OlFormatWKT from "ol/format/WKT";
import OlFeature from "ol/Feature";
import { DrawEvent } from "ol/interaction/Draw";
import OlDrawInteraction from "ol/interaction/Draw";
import { platformModifierKeyOnly } from "ol/events/condition";
import modelLokacije from "@/models/lokacije";
import { transform } from "ol/proj";
import { Extent as OlExtent, getCenter as OlGetCenter } from "ol/extent";

// Custom
import {
  ButtonControl,
  Controls,
  RotateControl,
  ScaleLineControl,
  ZoomControl,
  ZoomToExtentControl,
} from "@/components/Map/Controls";

import {
  Interactions,
  DrawInteraction,
  DefaultInteractions,
} from "@/components/Map/Interactions";
import { measurementsStyle, measuringStyle } from "@/components/Map/mapStyles";

import {
  Layers,
  TileLayer,
  VectorLayer,
  GroupLayer,
} from "@/components/Map/Layers";
import { Overlays, PopupOverlay } from "@/components/Map/Overlays";

import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwticher";
import GeoPortalGSLayer from "@/views/MapPage/GeoPortalGSLayer";
import {
  padExtent,
  mergeExtents,
  containedExtent,
  findInteraction,
} from "@/lib/olHelpers";
import modelFishnet from "@/models/fishnets";
import modelPhoto360 from "@/models/photos360";
import dataController from "@/lib/dataController";

//Services
import mapService from "@/services/mapService";
import gsService from "@/services/gsService";

import {
  selectedRecordStyle,
  objectAnalysisRecordStyle,
  fishnetStyle,
  imageInfoStyle,
  selectedImageInfoStyle,
  photo360Style,
  lokacijeStyle,
  qcHighlightedRecordStyle,
  qcRegularRecordStyle
} from "@/components/Map/mapStyles";
import { DCRecord, IDataController } from "@/@types/lib/dataController";

//Types
import OperationsContext, {
  OperationsContextType,
} from "@/components/OperationsContext/OperationsContext";
import {
  IMapSettings,
  IMapView,
  DrawingType,
  ViewOptionsType,
  BoolSettings,
} from "@/@types/common";
import copyTextToClipboard from "@/lib/copyTextToClipboard";
import {
  CardOverlayType,
  CardPositon,
  ICardOverlayData,
} from "@/@types/views/GIS/map";
import Image360InfoCard from "../../MapPage/Image360InfoCard";
import GSInfoCard, { CardPosition } from "../../MapPage/GSInfoCard";
import { MapBrowserEvent } from "ol";

import { DialogContextType } from "@/@types/ui/DialogContext";
import DialogContext from "@/ui/DialogContext/DialogContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";

import { GFIFeatureType } from "@/services/gsService";
import { Box } from "@mui/material";

type QualityControlMapProps = {
  records: Array<DCRecord>;
  qcRecords: Array<DCRecord>; // filteredRecords?
  selectedObjekt: DCRecord | null;
  lokacije: Array<DCRecord>;
  onLokacijeSelect: Function;
  selectedImageId: number | null;
  onObjektSelect: Function;
  imagesBasicInfo: Array<DCRecord>;
  mapId: number;
  selectedLokacija: DCRecord | null;
  refreshLocations: () => void;
  selectedObjektFor3D: DCRecord | null;
};

function QualityControlMap(props: QualityControlMapProps) {
  //#region Variable Definitions
  const dialogContext = useContext<DialogContextType>(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const operationsContext = useContext(
    OperationsContext
  ) as OperationsContextType;
  const { t } = useTranslation();

  const [mapInitialized, setMapInitialized] = useState(false);

  const [drawType, setDrawType] = useState<DrawingType | undefined>(undefined);
  const [drawingSource, setDrawingSource] = useState<OlSourceVector<Geometry>>(
    new OlSourceVector({})
  );
  const [measurementsSource, setMeasurementsSource] = useState<
    OlSourceVector<Geometry>
  >(new OlSourceVector({}));
  const [measuringFeature, setMeasuringFeature] = useState<
    OlFeature<Geometry> | undefined
  >(undefined);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [showLocations, setShowLocations] = useState<boolean>(false);
  const [showObjects, setShowObjects] = useState<boolean>(false);

  const [expandedPosjednici, setExpandedPosjednici] = useState(false);
  const [expandedVlasnici, setExpandedVlasnici] = useState(false);

  //@ts-ignore
  const {
    records,
    selectedObjekt,
    onObjektSelect,
    lokacije,
    onLokacijeSelect,
    imagesBasicInfo,
    selectedImageId,
    refreshLocations,
    selectedLokacija,
    selectedObjektFor3D,
    qcRecords
  } = props;
  const { mapId } = props;

  const [mapSettings, setMapSettings] = useState<IMapSettings | undefined>();
  const [defaultExtent, setDefaultExtent] = useState<number[] | undefined>();
  const [selectedExtent, setSelectedExtent] = useState(
    padExtent(defaultExtent)
  );
  const [objExtent, setObjExtent] = useState<number[]>([]);
  // const [imgExtent, setImgExtent] = useState<number[]>([]);
  const [currentExtent, setCurrentExtent] = useState<number[]>([]);
  const [viewOptions, setViewOptions] = useState<IMapView | undefined>();
  const [layersCollection, setLayersCollection] = useState<
    OlCollection<OlBaseLayer> | undefined
  >(undefined);
  const [objektiSource, setObjektiSource] = useState<OlSourceVector<Geometry>>(
    new OlSourceVector({})
  );
  const [finalQCRecords, setFinalQCRecords] = useState<DCRecord[]>([]);
  const [qcRecordsSource, setQCRecordsSource] = useState<OlSourceVector<Geometry>>(
    new OlSourceVector({})
  );
  const [lokacijeSource, setLokacijeSource] = useState<
    OlSourceVector<Geometry>
  >(new OlSourceVector({}));
  const [selectedSource, setSelectedSource] = useState<
    OlSourceVector<Geometry>
  >(new OlSourceVector({}));
  const [fishnetsSource, setFishnetsSource] = useState<
    OlSourceVector<Geometry>
  >(new OlSourceVector({}));
  const [imageInfoSource, setImageInfoSource] = useState<
    OlSourceVector<Geometry>
  >(new OlSourceVector({}));
  const [selectedImageInfoSource, setSelectedImageInfoSource] = useState<
    OlSourceVector<Geometry>
  >(new OlSourceVector({}));
  const [photos360Source, setPhotos360Source] = useState<
    OlSourceVector<Geometry>
  >(new OlSourceVector({}));
  const [showDOFLayer, setShowDOFLayer] = useState(true);
  const [showFishnetsLayers, setShowFishnetsLayer] = useState(false);
  const [showZgrade, setShowZgrade] = useState(true);
  const [showImageInfoLayers, setShowImageInfoLayers] = useState(true);
  const [cardOverlay, setCardOverlay] = useState<any | undefined>(undefined);
  const [events, setEvents] = useState<boolean>(false);

  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [project3d, setProject3d] = useState(false);

  const [feature, setFeature] = useState<OlFeature<Geometry> | undefined>(
    undefined
  );

  //define proj
  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
  proj4.defs(
    "EPSG:31276",
    "+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);

  const htrs96 = OlGetProjection("EPSG:3765") as Projection;
  const wgs84 = OlGetProjection("EPSG:4326") as Projection;
  const wgs84PM = OlGetProjection("EPSG:3857") as Projection;

  const wkt = new OlFormatWKT();

  const dcFishnets = new dataController(modelFishnet);
  const dcPhotos360 = new dataController(modelPhoto360);
  const dcLokacije: IDataController = new dataController(modelLokacije);

  // Threshold for when objects and locations show up
  const zoomThreshold = 18;
  const EXTENT_PADDING = 20;
  const Z_OFFSET = 90;

  //#endregion
  //#region handleClick()

  const handleObjektClick = (
    id: string | number | undefined,
    rec: DCRecord | null
  ) => {
    if (id) {
      onObjektSelect(id, rec);
    } else {
      onObjektSelect(-1, null);
    }
  };

  const handleDialogClose = (dataChanged: any) => {
    if (dataChanged.dataChanged === true && dataChanged.action === "insert") {
      refreshLocations();
      // window.location.reload();
    }
  };

  const generateCardOverlayData = (
    layer: GFIFeatureType[],
    featureType: CardOverlayType,
    pos: CardPositon
  ) => {
    return {
      position: pos,
      feature: layer[0],
      type: featureType,
      record: undefined,
    } as ICardOverlayData;
  };

  const handleClick = (evt: OlMapBrowserEvent<any>) => {
    if (drawType) return;
    let hit = false;

    var feature = evt.map.forEachFeatureAtPixel(
      evt.pixel,
      function (feature) {
        return feature;
      },
      {
        layerFilter: (layer) => {
          const layerId = layer.get("id");

          return (
            layerId !== null &&
            layerId !== undefined &&
            (layerId.startsWith("objekti") ||
              layerId.startsWith("lokacije") ||
              layerId.startsWith("photos360"))
          );
        },
      }
    );

    if (feature) {
      hit = true;

      const id = feature.getId();
      const rec = feature.getProperties();
      const { type } = rec || {};
      if (type && type === "photo360") {
        if (cardOverlay && cardOverlay.data.data.id === id) {
          closeOverlays(null);
        } else {
          const data = {
            position: evt.coordinate as CardPositon,
            data: rec,
            type: "photo360",
          } as ICardOverlayData;

          setCardOverlay({
            type: "photo360",
            data: data,
          });
        }
      } else if (rec?.wkt.startsWith("POINT") && id) {
        onLokacijeSelect(id, rec);
      } else {
        handleObjektClick(id, rec);
      }
    }

    if (!hit) {
      const dkpLayerInfo = layersCollection
        ?.getArray()
        .find((x) => x.get("id") === "app-dkp");
      if (dkpLayerInfo) {
        const gsLayerName = dkpLayerInfo.get("layer");

        gsService
          .getFeatureInfo(evt.map, evt.pixel, [gsLayerName])
          .then((resp) => {
            if (resp && Object.keys(resp).length != 0) {
              const layerKeys = Object.keys(resp);
              const dkpKey = layerKeys.find((x) => x.substring(4) === "DKP");
              const dkpFeatures = dkpKey ? resp[dkpKey] : [];

              if (dkpKey && dkpFeatures.length > 0) {
                const data = generateCardOverlayData(
                  resp[dkpKey],
                  "dkp",
                  evt.coordinate as CardPositon
                );
                const showCard = () => showCardOverlay("dkp", data, null);
                closeOverlays(showCard);
              }
            } else {
              closeOverlays(undefined);
            }
          });
      }
    }
  };

  //#endregion
  //#region useEffect()

  useEffect(() => {
    if (
      operationsContext.selectedProject?.cesium_folder !== null &&
      operationsContext.selectedProject?.cesium_config_file !== null
    ) {
      setProject3d(true);
    } else {
      setProject3d(false);
    }
  });

  useEffect(() => {
    if (layersCollection && mapSettings && viewOptions) {
      if (events) {
        unregisterEvents();
      }
      registerEvents();
      setEvents(true);
      return () => unregisterEvents();
    }
    return () => {};
  }, [layersCollection, mapSettings, viewOptions, selectedSource]);

  useEffect(() => {
    setMapSettings(undefined);
    setViewOptions(undefined);
    setSelectedSource(new OlSourceVector({}));

    if (operationsContext.selectedProject) {
      const projectId = operationsContext.selectedProject.id as number;
      mapService.getDefaultData(mapId, projectId).then((resp) => {
        if (resp) {
          setMapSettings(resp);
        } else {
          setMapSettings(undefined);
        }
      });

      mapService.getLayers(mapId, projectId).then((coll) => {
        setLayersCollection(coll);
        setMapInitialized(true);
      });

      dcFishnets
        .GetData(
          `operations/projekti/${operationsContext.selectedProject.id}/fishnets/`
        )
        .then((resp) => {
          if (resp.success) {
            const src = new OlSourceVector({});
            if (Array.isArray(resp.data) && resp.data.length > 0) {
              resp.data.forEach((x) => {
                let ft = wkt.readFeature(x.wkt, {
                  dataProjection: htrs96,
                  featureProjection: wgs84PM,
                });
                if (ft) {
                  ft.setId(x.id as number);
                  ft.setProperties({ id: x.id, sifra: x.sifra });
                  src.addFeature(ft);
                }
              });
            }
            setFishnetsSource(src);
          }
        });

      dcPhotos360
        .GetData(
          `operations/projekti/${operationsContext.selectedProject.id}/photos360/`
        )
        .then((resp) => {
          if (resp.success) {
            const src = new OlSourceVector({});
            if (Array.isArray(resp.data) && resp.data.length > 0) {
              resp.data.forEach((x) => {
                let ft = wkt.readFeature(x.wkt, {
                  dataProjection: wgs84PM,
                  featureProjection: wgs84PM,
                });
                if (ft) {
                  ft.setId(x.id as number);
                  ft.setProperties({
                    id: x.id,
                    fileName: x.filename,
                    type: "photo360",
                  });
                  src.addFeature(ft);
                }
              });
            }
            setPhotos360Source(src);
          }
        });
    } else {
      setFishnetsSource(new OlSourceVector({}));
      setPhotos360Source(new OlSourceVector({}));
    }
  }, [operationsContext.selectedProject]);

  useEffect(() => {
    if (mapSettings) {
      setDefaultExtent(mapSettings.default_extent);
      setSelectedExtent(padExtent(mapSettings.default_extent));
      setViewOptions((prevState) => {
        return {
          ...prevState,
          center: mapSettings.initial_view_center,
          zoom: mapSettings.initial_view_zoom,
          extent: mapSettings.max_extent,
          minZoom: 8,
          maxZoom: 23,
        };
      });
    }
  }, [mapSettings]);

  // Objects in fishnet
  useEffect(() => {
    const src = new OlSourceVector({});
    if (Array.isArray(records)) {
      records.forEach((record: DCRecord) => {
        if (record.wkt) {
          let newFeature = new OlFeature({
            geometry: wkt.readGeometry(record.wkt, {
              dataProjection: htrs96,
              featureProjection: wgs84PM,
            }),
          });
          newFeature.setId(record.id as number);
          newFeature.setProperties({ ...record });
          src.addFeature(newFeature);
        }
      });
    }

    setObjektiSource(src);
  }, [records]);

  // add geom and other attributes to QCRecords -> finalQCRecords
  useEffect(() => {
    
    if (Array.isArray(qcRecords) && Array.isArray(records)) {
      const mergedQCRecords = qcRecords.map((qcRecord: DCRecord) => {
        const match = records.find((r: DCRecord) => r.id === qcRecord.id);
        return match ? { ...match, ...qcRecord } : qcRecord;
      });

      setFinalQCRecords(mergedQCRecords);
    }
  }, [qcRecords, records]);

  // add finalQCRecords to source for vector layer
  useEffect(() => {
    const src = new OlSourceVector({});

    finalQCRecords.forEach((record: DCRecord) => {
      if (record.wkt) {
        const newFeature = new OlFeature({
          geometry: wkt.readGeometry(record.wkt, {
            dataProjection: htrs96,
            featureProjection: wgs84PM,
          }),
        });
        newFeature.setId(record.id as number);
        newFeature.setProperties({ ...record });
        src.addFeature(newFeature);
      }
    });

    setQCRecordsSource(src);
  }, [finalQCRecords])

  // Locations
  useEffect(() => {
    const src = new OlSourceVector({});
    if (Array.isArray(lokacije)) {
      lokacije.forEach((record: DCRecord) => {
        if (record.wkt) {
          let newFeature = new OlFeature({
            geometry: wkt.readGeometry(record.wkt, {
              dataProjection: htrs96,
              featureProjection: wgs84PM,
            }),
          });
          newFeature.setId(record.id as number);
          newFeature.setProperties({ ...record });

          src.addFeature(newFeature);
        }
      });
    }

    setLokacijeSource(src);
  }, [lokacije]);

  useEffect(() => {
    const src = new OlSourceVector({});
    let hasWkt = false;
    imagesBasicInfo.forEach((imgInfo: DCRecord) => {
      if (imgInfo.wkt) {
        hasWkt = true;
        let newFeature = new OlFeature({
          geometry: wkt.readGeometry(imgInfo.wkt, {
            dataProjection: htrs96,
            featureProjection: wgs84PM,
          }),
        });
        newFeature.setId(imgInfo.imageId as number);
        newFeature.setProperties({ ...imgInfo });
        src.addFeature(newFeature);
      }
    });
    setImageInfoSource(src);
    setSelectedImageInfoSource(new OlSourceVector({}));
    // if (imagesBasicInfo.length && hasWkt) {
    //   setImgExtent(src.getExtent());
    // }
  }, [imagesBasicInfo]);

  useEffect(() => {
    if (showImageInfoLayers) {
      let hasWkt = false;
      const src = new OlSourceVector({});
      const selId = selectedImageId ? selectedImageId : null;
      const selected = imagesBasicInfo.find((img) => img.imageId === selId);
      if (selId && selected && selected.wkt) {
        hasWkt = true;
        let newFeature = new OlFeature({
          geometry: wkt.readGeometry(selected.wkt, {
            dataProjection: htrs96,
            featureProjection: wgs84PM,
          }),
        });
        newFeature.setId(selected.imageId as number);
        newFeature.setProperties({ ...selected });
        src.addFeature(newFeature);
      }
      setSelectedImageInfoSource(src);
      // const imgSelected = selected && selected.wkt;
      // if (src && objExtent.length > 0 && imgSelected && hasWkt) {
      //   const tmpImgExtent = src.getExtent();
      //   if (!containedExtent(tmpImgExtent, currentExtent)) {
      //     setImgExtent(mergeExtents(objExtent, tmpImgExtent, true));
      //   }
      // }
    }
  }, [selectedImageId]);

  useEffect(() => {
    if (
      showImageInfoLayers &&
      imageInfoSource &&
      imageInfoSource.getFeatures().length &&
      imagesBasicInfo.length > 0
    ) {
      setObjExtent([]);
      // setImgExtent(imageInfoSource.getExtent());
      if (drawType !== undefined) {
        toggleDrawType();
      }
    }
  }, [showImageInfoLayers]);

  useEffect(() => {
    const src = new OlSourceVector({});
    if (
      selectedObjekt != null &&
      selectedObjekt.wkt !== undefined &&
      selectedObjekt.wkt !== null
    ) {
      let ft = wkt.readFeature(selectedObjekt.wkt, {
        dataProjection: htrs96,
        featureProjection: wgs84PM,
      });
      if (ft) {
        ft.setId(selectedObjekt.id as number);
        src.addFeature(ft);
      }
      setShowImageInfoLayers(true);
    }
    setSelectedSource(src);
    if (src && selectedObjekt && selectedObjekt.wkt) {
      setObjExtent([]);
      const extent = padExtent(src.getExtent(), EXTENT_PADDING);
      if (extent[0] === extent[2] && extent[1] === extent[3]) {
        // Adjust zoom for locations - possibly dumb
        extent[0] = extent[0] - 20;
        extent[2] = extent[2] + 20;
        extent[1] = extent[1] - 20;
        extent[3] = extent[3] + 20;
      }
      // console.log(extent);
      setSelectedExtent(extent);
    } else {
      /*
      if (defaultExtent) {
        setSelectedExtent(defaultExtent);
      }
      */
    }
  }, [selectedObjekt]);

  //#endregion
  //#region Helpers

  const eventRegistrationFn = (evt: KeyboardEvent) => {
    if (evt.altKey) {
      switch (evt.code) {
        case "KeyZ":
        case "KeyY":
          setShowImageInfoLayers((prev) => !prev);
          break;
        case "KeyX":
          toggleDrawType();
          break;
        case "KeyC":
          setObjExtent(padExtent(selectedSource.getExtent(), EXTENT_PADDING));
          break;
        case "KeyO":
          setShowObjects((showObjects) => !showObjects);
          break;
        case "KeyL":
          setShowLocations((showLocations) => !showLocations);
          break;
        case "KeyD": {
          if (project3d) {
            evt.preventDefault();
            toggleView3d();
          }
          break;
        }
        // case 'Digit1': handleFilter(1); break;
        // case 'Digit2': handleFilter(2); break;
        // case 'Digit3': handleFilter(3); break;
        // case 'Digit4': handleFilter(4); break;
        // case 'Digit5': handleFilter(5); break;
        // case 'Digit6': handleFilter(6); break;
      }
    }
  };

  const registerEvents = () => {
    window.addEventListener("keydown", eventRegistrationFn);
  };

  const unregisterEvents = () => {
    window.removeEventListener("keydown", eventRegistrationFn);
  };

  const handleLayerSwitcherDOFClick = () => {
    setShowDOFLayer((prevState) => {
      return !prevState;
    });
  };

  const handleLayerSwitcherFishnetClick = () => {
    setShowFishnetsLayer((prevState) => {
      return !prevState;
    });
  };

  const toggleZgrade = () => {
    setShowZgrade((prevState) => {
      return !prevState;
    });
  };

  const handleDrawMeasureStart = (evt: DrawEvent) => {
    setMeasurementsSource(
      (prevState: OlSourceVector<Geometry>) =>
        new OlSourceVector({
          features: [],
        })
    );
    if (drawingSource.getFeatures().length > 0) {
      setDrawingSource(new OlSourceVector({}));
    }

    setMeasuringFeature(evt.feature);
  };

  const handleDrawMeasureEnd = (evt: DrawEvent) => {
    setDrawingSource(new OlSourceVector({}));
    setMeasurementsSource(
      (prevState: OlSourceVector<Geometry>) =>
        new OlSourceVector({
          features: [evt.feature],
        })
    );

    setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
  };

  const drawCondition = (evt: MapBrowserEvent<any>) => {
    var isCtrl = platformModifierKeyOnly(evt);
    if (isCtrl) {
      const int = findInteraction(evt.map, OlDrawInteraction);
      if (int) {
        // console.log("removing last point...");
        (int as OlDrawInteraction).removeLastPoint();
      }
    }
    return isCtrl ? false : true;
  };

  const handleEraseMeasurements = () => {
    setMeasurementsSource(new OlSourceVector({}));
  };

  const toggleDrawType = () => {
    setDrawType((prevState: DrawingType | undefined) => {
      const newState = prevState === undefined ? "Polygon" : undefined;
      if (!newState) {
        handleEraseMeasurements();
        setDrawingSource(new OlSourceVector({}));
        setShowImageInfoLayers(true);
      } else {
        setShowImageInfoLayers(false);
        if (selectedSource.getFeatures().length > 0) {
          const extent = padExtent(selectedSource.getExtent());
          // setObjExtent(extent);
        }
      }
      return newState;
    });
  };

  const copyValue = (n: number) => {
    copyTextToClipboard(`${n.toFixed(2)}`);
  };

  const showCardOverlay = (
    type: CardOverlayType,
    data: ICardOverlayData,
    ft: OlFeature<any> | null
  ) => {
    //TODO: refactor
    feature ? feature.setProperties({ selected: false }) : null;

    if (ft) {
      ft.setProperties({ selected: true });
    }

    setCardOverlay({
      type: type,
      data: data,
    });
    if (ft) {
      setFeature(ft);
    } else {
      setFeature(undefined);
    }
  };

  const closeOverlays = (callback: any) => {
    setCardOverlay(undefined);
    setExpandedPosjednici(false);
    setExpandedVlasnici(false);
    if (callback !== undefined && typeof callback === "function") {
      callback();
    }
  };

  const handleZoomChange = (zoom: number) => {
    if (zoomLevel === 0) {
      setZoomLevel(zoom);
      return;
    }
    if (zoom > zoomThreshold && zoomLevel < zoomThreshold) {
      setShowObjects(true);
      setShowLocations(true);
    } else if (zoom < zoomThreshold && zoomLevel > zoomThreshold) {
      setShowObjects(false);
      setShowLocations(false);
    }

    setZoomLevel(zoom);
  };

  const toggleView3d = () => {
    if (
      selectedObjektFor3D &&
      selectedObjektFor3D !== null
    ) {
      const ft = selectedSource
        .getFeatures()
        .find((x) => x.getId() === selectedObjektFor3D?.id);
      const extent = ft?.getGeometry()?.getExtent() as OlExtent;

      let cx, cy, cz;

      if (selectedObjektFor3D.cx === null || selectedObjektFor3D.cy === null) {
        // NEMA CENTROIDA :(
        [cx, cy] = transform(
          [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
          "EPSG:3857",
          "EPSG:4326"
        ).map((x) => (x * Math.PI) / 180);
        cz = 150; // statična visina jer nemamo podatak o visini
      } // IMA CENTROIDA :)
      else {
        [cx, cy] = transform(
          [
            selectedObjektFor3D?.cx as number,
            selectedObjektFor3D?.cy as number,
          ],
          "EPSG:3765",
          "EPSG:4326"
        ).map((x) => (x * Math.PI) / 180);
        cz = selectedObjektFor3D?.cz
          ? (selectedObjektFor3D.cz as number) + Z_OFFSET
          : Z_OFFSET;
      }

      const url =
        location.origin +
        "/3d-view?cx=" +
        cx?.toString() +
        "&cy=" +
        cy?.toString() +
        "&cz=" +
        cz?.toString() +
        "&pid=" +
        operationsContext.selectedProject?.id?.toString();

      window.open(url, "_blank"); // opens 3d in a new tab
    } else {
      if (snackbarContext) {
        snackbarContext.showNotification(t("users.no_3d_object"), "warning");
      }
    }
  };

  const image360Overlay =
    cardOverlay && cardOverlay.type === "photo360" ? cardOverlay : undefined;

  // const gsOverlay = cardOverlay && cardOverlay.type === "gsinfo" ? cardOverlay : undefined;
  const gsOverlay =
    cardOverlay && cardOverlay.type === "dkp" ? cardOverlay : undefined;

  //#endregion
  //#region Return

  return (
    <Box sx={{ width: "100%", height: "100%" /*minHeight: 500*/ }}>
      {layersCollection && mapSettings && viewOptions ? (
        <Map
          height={"2"}
          view={viewOptions}
          onClick={handleClick}
          //onPointermove={handlePointerMove}
          className="map"
          id="mini-map"
          zoomToExtent={objExtent.length ? objExtent : selectedExtent}
          onMoveend={({ map }: OlMapBrowserEvent<any>) => {
            const view = map ? map.getView() : null;
            const extent = view ? view.calculateExtent() : null;
            if (extent && extent.length === 4) {
              setCurrentExtent([...extent]);
            }
          }}
          // zoomToExtentPadding={[50, 50, 50, 50]}
          disableExtentPadding={true}
          initialized={mapInitialized}
          zoomTrigger={handleZoomChange}
        >
          <Controls>
            <ZoomControl
              zoomInTipLabel={t("map:controls.zoom_in")}
              zoomOutTipLabel={t("map:controls.zoom_out")}
            />
            <RotateControl
              tipLabel={`${t("map:info.reset-rotation")}. ${t(
                "map:info.rotate-the-map"
              )}`}
              autoHide={true}
            />
            <ScaleLineControl />
            <GeoBaseLayerSwitcher allowNoLayer display={!showDOFLayer} />
            <ZoomToExtentControl
              id="zoom-extent-default"
              extent={defaultExtent}
              tipLabel={t("map:controls.zoom_to_extent")}
              className="ol-control ol-zoom-extent"
            />
            <ZoomToExtentControl
              id="zoom-extent-selected"
              extent={selectedExtent}
              tipLabel={t("map:controls.zoom_to_selected_shortcut")}
              className="ol-control ol-zoom-selected"
              labelClass="fas fa-bullseye"
            />
            <ButtonControl
              id="layerswitcher-dof"
              className={`ol-control ol-layerswitcher-dof ${
                showDOFLayer ? "ol-active" : ""
              }`}
              html="<i class='fas fa-camera'></i>"
              handleClick={handleLayerSwitcherDOFClick}
              title={t("map:layerswitcher.toggle_dof")}
            />
            <ButtonControl
              id="layerswitcher-fishnet"
              className={`ol-control ol-layerswitcher-fishnet ${
                showFishnetsLayers ? "ol-active" : ""
              }`}
              html="<i class='fas fa-border-all'></i>"
              handleClick={handleLayerSwitcherFishnetClick}
              title={t("map:layerswitcher.toggle_fishnet")}
            />
            <ButtonControl
              id="layerswitcher-measure"
              className={`ol-control ol-measure-button-alt ${
                drawType !== undefined ? "ol-active" : ""
              }`}
              html="<i class='fas fa-pencil-ruler'></i>"
              handleClick={toggleDrawType}
              title={t("map:layerswitcher.toggle_measure")}
            />
            {layersCollection
              .getArray()
              .find(
                (x) =>
                  x.get("id") === "app-zgrade" || x.get("id") === "app-zgr_op"
              ) ? (
              <ButtonControl
                id="toggle-zgrade"
                className={`ol-control ol-layerswitcher-zgrade-alt ${
                  showZgrade ? "ol-active" : ""
                }`}
                html="<i class='fas fa-building'></i>"
                handleClick={toggleZgrade}
                title={t("map:layerswitcher.toggle-zgrade")}
              />
            ) : null}
            <ButtonControl
              id="layerswitcher-show-locations"
              className={`ol-control ol-show-locations ${
                showLocations === true ? "ol-active" : ""
              }`}
              html="<i class='fas fa fa-map-marker'></i>"
              handleClick={() => {
                setShowLocations(!showLocations);
              }}
              title={t("map:layerswitcher.show-locations")}
            />
            <ButtonControl
              id="layerswitcher-show-objects"
              className={`ol-control ol-show-objects ${
                showObjects === true ? "ol-active" : ""
              }`}
              html="<i class='fas fa-object-group'></i>"
              handleClick={() => {
                setShowObjects(!showObjects);
              }}
              title={t("map:layerswitcher.show-objects")}
            />
            <ButtonControl
              id="view-3d"
              className={`ol-control ol-3d-view-alt ${
                selectedObjektFor3D ? "" : "ol-inactive-3d"
              } ${project3d ? "" : "ol-nonexistant-3d"}`}
              html={"<i class='fas fa-globe-americas'></i>"}
              handleClick={toggleView3d}
              title={t("map:layerswitcher.toggle_3d")}
            />
          </Controls>
          <Layers>
            {layersCollection ? (
              <>
                {layersCollection
                  .getArray()
                  .map((layer: OlBaseLayer, i: number) => {
                    const id = layer.get("id");
                    const visible =
                      id === "app-dof"
                        ? showDOFLayer
                        : id === "app-zgrade" || id === "app-zgr_op"
                        ? showZgrade
                        : true;

                    return (
                      <GeoPortalGSLayer
                        key={i}
                        id={id}
                        title={t(layer.get("title"))}
                        layer={layer}
                        query={layer.get("query")}
                        zIndex={layer.get("zIndex")}
                        visible={visible}
                      />
                    );
                  })}
              </>
            ) : null}
            <VectorLayer
              id="fishnets"
              source={fishnetsSource}
              style={fishnetStyle}
              zIndex={800}
              visible={showFishnetsLayers}
            />
            <VectorLayer
              id="lokacije"
              source={lokacijeSource}
              style={showLocations === true ? lokacijeStyle : null}
              zIndex={902}
            />
            <VectorLayer
              id="objekti"
              source={objektiSource}
              style={showObjects === true ? qcRegularRecordStyle : null}
              zIndex={900}
            />
            <VectorLayer
              id="qc_objekti"
              source={qcRecordsSource}
              style={qcHighlightedRecordStyle}
              zIndex={949}
            />
            <VectorLayer
              id="photos360"
              source={photos360Source}
              style={photo360Style}
              zIndex={905}
              visible={true}
            />
            <VectorLayer
              id="image-info"
              source={imageInfoSource}
              style={imageInfoStyle}
              zIndex={910}
              visible={showImageInfoLayers}
            />
            <VectorLayer
              id="selected"
              source={selectedSource}
              style={selectedRecordStyle}
              zIndex={950}
            />
            <VectorLayer
              id="image-info-selected"
              source={selectedImageInfoSource}
              style={selectedImageInfoStyle}
              zIndex={960}
              visible={showImageInfoLayers}
            />
            <VectorLayer
              id="measurements"
              source={measurementsSource}
              style={(f: any, ...rest) => (
                //@ts-ignore
                (f.__callback = copyValue), measurementsStyle(f, ...rest)
              )}
              zIndex={900}
              // title="Temp measurements"
            />
            {/* <VectorLayer id="highlighted" source={highlightedSource} style={hoveringRecordStyle} zIndex={960} /> */}
          </Layers>
          <Overlays>
            <PopupOverlay
              id="feature-overlay"
              position={
                image360Overlay
                  ? image360Overlay.data.position
                  : gsOverlay
                  ? gsOverlay.data.position
                  : undefined
              }
              autoPan={false}
              onClose={() => {}}
            >
              {cardOverlay && gsOverlay ? (
                <GSInfoCard
                  featureData={gsOverlay.data}
                  onClose={closeOverlays}
                  expandedVlasnici={expandedVlasnici}
                  setExpandedVlasnici={setExpandedVlasnici}
                  expandedPosjednici={expandedPosjednici}
                  setExpandedPosjednici={setExpandedPosjednici}
                />
              ) : null}
              {image360Overlay ? (
                <Image360InfoCard
                  id={image360Overlay.data.data.id}
                  fileName={image360Overlay.data.data.fileName}
                  onClose={closeOverlays}
                />
              ) : null}
            </PopupOverlay>
          </Overlays>
          <Interactions>
            <DefaultInteractions />
            {drawType ? (
              <DrawInteraction
                source={drawingSource}
                type={drawType}
                style={measuringStyle}
                onDrawstart={handleDrawMeasureStart}
                onDrawend={handleDrawMeasureEnd}
                condition={drawCondition}
              />
            ) : null}
          </Interactions>
        </Map>
      ) : (
        <Skeleton
          animation={false}
          sx={{
            width: "100%",
            height: "100%",
            transform: "unset" /*minHeight: 500*/,
          }}
        />
      )}
    </Box>
  );
}

export default QualityControlMap;
